import { useMutation, useQuery } from '@tanstack/react-query';

import { canvasLayoutApi } from '@/services/api/canvas-layout';
import type {
  CanvasAssetType,
  CanvasContextType,
  CanvasLayoutItemsPatchRequest,
  CanvasLayoutNodeRecordRequest,
} from '@/types/canvas';

export const canvasLayoutKeys = {
  all: ['canvas-layout'] as const,
  context: (
    projectId: string,
    contextType: CanvasContextType,
    assetType?: CanvasAssetType | null,
    assetId?: string | null,
    storyboardId?: string | null
  ) =>
    [
      ...canvasLayoutKeys.all,
      projectId,
      contextType,
      assetType ?? null,
      assetId ?? null,
      storyboardId ?? null,
    ] as const,
};

export function useCanvasLayout(params: {
  projectId: string | null;
  contextType: CanvasContextType;
  assetType?: CanvasAssetType | null;
  assetId?: string | null;
  storyboardId?: string | null;
  enabled?: boolean;
}) {
  const enabled = (params.enabled ?? true) && Boolean(params.projectId);

  return useQuery({
    queryKey: params.projectId
      ? canvasLayoutKeys.context(
          params.projectId,
          params.contextType,
          params.assetType,
          params.assetId,
          params.storyboardId
        )
      : canvasLayoutKeys.all,
    enabled,
    queryFn: () => {
      if (!params.projectId) {
        throw new Error('Missing projectId');
      }
      return canvasLayoutApi.getLayout({
        projectId: params.projectId,
        contextType: params.contextType,
        assetType: params.assetType,
        assetId: params.assetId,
        storyboardId: params.storyboardId,
      });
    },
  });
}

/**
 * PATCH returns a lightweight write result and intentionally does not overwrite layout cache.
 */
export function usePatchCanvasLayoutItems() {
  return useMutation({
    mutationFn: ({
      projectId,
      payload,
    }: {
      projectId: string;
      payload: CanvasLayoutItemsPatchRequest;
    }) => canvasLayoutApi.patchLayoutItems(projectId, payload),
  });
}

/**
 * PATCH canvas node records without shared position fields.
 * Layout positions are stored in the current user's local cache.
 */
export function usePatchCanvasLayoutNodeRecords() {
  return useMutation({
    mutationFn: ({
      projectId,
      payload,
    }: {
      projectId: string;
      payload: CanvasLayoutNodeRecordRequest;
    }) => canvasLayoutApi.patchNodeRecords(projectId, payload),
  });
}
